import { Helmet } from 'react-helmet-async';
import type { PublicSupplierProfileVO } from '../../../types/model/valueObject/account/PublicSupplierProfileVO';
import { getSchemaTypes } from '../../location/profile/LocationProfileSEO';
import NotFoundHead from '../../seo/NotFoundHead';
import { DEFAULT_IMAGE, useClaimHead } from '../../seo/HeadDefaults';

interface SupplierProfileSEOProps {
  profile: PublicSupplierProfileVO | null;
  /** The supplier doesn't exist: keep the page out of search (securspace-ui#3107). */
  notFound?: boolean;
}

const SITE = 'https://secur.space';

export const supplierCanonicalUrl = (profile: Pick<PublicSupplierProfileVO, 'shortId' | 'slug'>): string =>
  `${SITE}/supplier/${profile.shortId}/${profile.slug}`;

/** Organization JSON-LD for a supplier, listing its yards as places it operates. */
export const buildSupplierStructuredData = (profile: PublicSupplierProfileVO) => {
  const url = supplierCanonicalUrl(profile);
  const locations = profile.locations || [];
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${url}#organization`,
    name: profile.companyName,
    url,
    logo: profile.logoUrl || undefined,
    description: profile.companyDescription || undefined,
    foundingDate: profile.yearFounded ? String(profile.yearFounded) : undefined,
    // Parent of every listed yard, so a location's SelfStorage/ParkingFacility and this
    // Organization resolve to the same graph.
    // Same type derivation as the location page, so the shared @id never
    // carries conflicting types across the two documents.
    subOrganization: locations.map(l => ({
      '@type': getSchemaTypes(l.equipmentTypes),
      '@id': `${SITE}/location-profile/${l.locationId}`,
      name: l.locationName,
      url: `${SITE}/location-profile/${l.locationId}`,
      address: { '@type': 'PostalAddress', addressLocality: l.city, addressRegion: l.state, addressCountry: 'US' },
    })),
  };
};

const SupplierProfileSEO = ({ profile, notFound = false }: SupplierProfileSEOProps) => {
  useClaimHead();
  if (notFound) return <NotFoundHead title="Supplier not found | SecurSpace" />;
  const companyName = profile?.companyName || '';
  const description = profile?.companyDescription
    ? `${profile.companyDescription.substring(0, 155)}…`
    : `${companyName} — truck parking, trailer storage, and container yards on SecurSpace.`;
  // No profile (loading or not found): no canonical, rather than one pointing elsewhere.
  const canonicalUrl = profile?.shortId ? supplierCanonicalUrl(profile) : undefined;
  const title = companyName
    ? `${companyName} — Truck Parking & Storage Locations | SecurSpace`
    : 'Supplier Profile | SecurSpace';
  const logoUrl = profile?.logoUrl || '';

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={`${companyName}, truck parking, container storage, trailer parking, intermodal storage`} />
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SecurSpace" />
      <meta property="og:image" content={logoUrl || DEFAULT_IMAGE} />
      {profile && (
        <script type="application/ld+json">
          {JSON.stringify(buildSupplierStructuredData(profile))}
        </script>
      )}
    </Helmet>
  );
};

export default SupplierProfileSEO;
